import { Injectable, inject } from '@angular/core';
import { cloneDeep, first } from 'lodash';
import {
  ConvertUnitRequest,
  ConvertUnitsApi,
  ConvertUnitsService,
  ConvertUnitValue,
  SelectItemV2,
  Unit,
  UnitsService,
  UnitsV2,
  UnitType,
  UnitV2,
} from '@schenck/rotec-components';
import { SI_UNITS, UNITS_MAPPERS } from 'src/utils/constants/units.const';
import { ConversionTypes } from 'src/utils/enums/units.enum';
import { concatMap, from, map, Observable, of, toArray } from 'rxjs';
import { UnitType as SIUnitType, WeightSetItem } from 'src/utils/interfaces/rotor-type.interfaces';

@Injectable({
  providedIn: 'root',
})
export class UnitsHelperService {
  private readonly convertUnitsService = inject(ConvertUnitsService);
  private readonly convertUnitsApi = inject(ConvertUnitsApi);
  private readonly unitsService = inject(UnitsService);

  private conversionType: ConversionTypes;

  public convertUnits({
    values,
    conversionType,
  }: {
    values: SelectItemV2<string | number | boolean>[];
    conversionType: ConversionTypes;
  }): void {
    this.conversionType = conversionType;

    const valuesToConvert = this.getConvertedUnits(values).filter(
      (data) => data.originUnit !== data.targetUnit && data.values && data.targetUnit >= 0
    );

    if (valuesToConvert.length) {
      this.convertUnitsService.convertUnits$(valuesToConvert);
    } else {
      this.convertUnitsService.resetConvertedUnits$();
    }
  }

  public getConvertedUnits(
    values: SelectItemV2<string | number | boolean>[]
  ): ConvertUnitRequest[] {
    const numberValues = values.filter(
      ({ value }) => typeof value === 'number'
    ) as SelectItemV2<number>[];

    return this.unitsService.state.reduce((totalRequests, { units, group }) => {
      const filteredValues: SelectItemV2<number>[] = numberValues.filter(
        ({ unit }) => !!units.find((item) => item.name === unit)
      );

      const filteredValuesByUnit: Record<string, SelectItemV2<number>[]> =
        this.getValuesByUnit(filteredValues);
      const requestsForUnitGroup: ConvertUnitRequest[] = Object.keys(filteredValuesByUnit).map(
        (key) =>
          this.mapToConvertRequest({
            values: filteredValuesByUnit[key],
            unitGroup: { units, group },
            type: UNITS_MAPPERS[group],
          })
      );

      return [...totalRequests, ...requestsForUnitGroup];
    }, [] as ConvertUnitRequest[]);
  }

  /**
   * Convert the weight to the origin unit if it's not in kg.
   * @param value
   */
  public getWeightOrigin$(weight: SIUnitType): Observable<number> {
    // if the weight is already in kg, return it
    if (weight.orgUnit.symbol === 'kg') {
      return of(weight.siValue);
    } else {
      return this.convertUnitsService.convertUnit$({
        group: `EPhysUnit_Mass`,
        decimalDigits: 10,
        originUnit: this.unitsService.getUnitIndex('kg', 'Mass'),
        targetUnit: weight.orgUnit.unitIndex,
        values: [
          {
            key: 'weight',
            value: weight.siValue,
          },
        ],
      });
    }
  }

  /**
   * Convert the weights to the origin unit
   * The weights units should all be in kg.
   * @param value
   */
  public getWeightsOrigin$(weights: SIUnitType[]): Observable<ConvertUnitValue[]> {
    // if the weight is already in kg, return it
    if (weights[0].orgUnit.symbol === 'kg') {
      const originalWeights: ConvertUnitValue[] = weights.map((weight, index) => ({
        key: index.toString(),
        value: weight.siValue,
        convertedValue: weight.siValue,
      }));
      return of(originalWeights);
    }

    return this.convertUnitsApi.convertUnits$({
      group: `EPhysUnit_Mass`,
      decimalDigits: 10,
      originUnit: this.unitsService.getUnitIndex('kg', 'Mass'),
      targetUnit: weights[0].orgUnit.unitIndex,
      values: weights.map((weight, index) => ({
        key: index.toString(),
        value: weight.siValue,
      })),
    });
  }

  /**
   * Add the origin weights to the weight set items and return sorted by si value
   * This function makes sure that only one API request is made for each unit
   * @param weights
   * @returns
   */
  public getWeightsWithOrigin$(weights: WeightSetItem[]): Observable<WeightSetItem[]> {
    const weightMap = new Map<Unit, WeightSetItem[]>();
    // To be able to reduce the number of possible API Requests,
    // we first group the weights by their unit
    weights.forEach((weight) => {
      weightMap.set(weight.mass.value.orgUnit.symbol, [
        ...(weightMap.get(weight.mass.value.orgUnit.symbol) || []),
        weight,
      ]);
    });
    const groupedWeights = Array.from(weightMap.values());

    return from(groupedWeights).pipe(
      // by using concatMap we can ensure that the order of the weights is preserved
      concatMap((weightSetItems) =>
        this.getWeightsOrigin$(weightSetItems.map((item) => item.mass.value))
      ),
      toArray(), // Collect all results into an array
      map((groupedConvertedWeights) => {
        const groupedWeightsCopy = cloneDeep(groupedWeights);
        for (let i = 0; i < groupedConvertedWeights.length; i++) {
          for (let k = 0; k < groupedConvertedWeights[i].length; k++) {
            // In the inner loop we have to find the original weight, because
            // we cannot ensure that the convert API returns the converted values
            // in the same order as the input, therefore we use the key to find the
            // correct weight
            groupedWeightsCopy[i][k].mass.orgValue = groupedConvertedWeights[i].find(
              (item) => item.key === k.toString()
            ).convertedValue;
          }
        }

        const flattenWeights: WeightSetItem[] = [];

        groupedWeightsCopy.forEach((group) => {
          flattenWeights.push(...group);
        });

        return flattenWeights.sort((a, b) => a.mass.value.siValue - b.mass.value.siValue);
      })
    );
  }

  private mapToConvertRequest({
    values,
    unitGroup,
    type,
  }: {
    values: SelectItemV2[];
    unitGroup: UnitsV2;
    type: string;
  }): ConvertUnitRequest {
    let request: Partial<ConvertUnitRequest> = {
      decimalDigits: 12,
      type,
      group: unitGroup.group,
      values: values
        .filter(({ unit }) => unitGroup.units.map(({ name }) => name).includes(unit as Unit))
        .map(({ name, value, subname }) => ({ key: name, value: +subname || +value })),
    };

    switch (this.conversionType) {
      case ConversionTypes.Response:
        request = {
          ...request,
          originUnit: this.getUnitIndex(SI_UNITS[type], unitGroup.units),
          targetUnit: this.getUnitIndex(first(values)?.unit, unitGroup.units),
        };
        break;

      case ConversionTypes.Saving:
        request = {
          ...request,
          originUnit: this.getUnitIndex(first(values)?.unit, unitGroup.units),
          targetUnit: this.getUnitIndex(SI_UNITS[type], unitGroup.units),
        };
        break;
    }

    return <ConvertUnitRequest>request;
  }

  private getUnitIndex(unit: string, units: UnitV2[]): number {
    return units.findIndex(({ name }) => name === unit);
  }

  private getValuesByUnit(values: SelectItemV2[]): Record<string, SelectItemV2<number>[]> {
    return values.reduce((acc, item) => {
      return {
        ...acc,
        [item.unit]: acc[item.unit] ? [...acc[item.unit], item] : [item],
      };
    }, {});
  }

  //---------- Converts the single value ----------//

  public convertUnit$(
    item: SelectItemV2<number>,
    unitType: UnitType,
    targetUnit: Unit
  ): Observable<number> {
    return this.convertUnitsService.convertUnit$({
      group: `EPhysUnit_${unitType}`,
      decimalDigits: 10,
      originUnit: this.unitsService.getUnitIndex(item.unit as Unit, unitType),
      targetUnit: this.unitsService.getUnitIndex(targetUnit, unitType),
      values: [
        {
          key: item.name,
          value: item.value,
        },
      ],
    });
  }
}
